import { PageLayout } from "@/components";
import { useTranslation } from "@/hooks/useTranslation";
import OrdersContent from "@/pages/orders/OrdersContent";
import { PaymentReceived, PaymentSent } from "@/pages/payment/index/components";
import AddPaymentDialog from "@/pages/payment/shared/AddPaymentDialog";
import { useDecideRelation, useRelation } from "@/services";
import { formatNumber } from "@/utils/tools";
import { getRelationStateTranslation } from "@/utils/translations";
import {
	Button,
	Card,
	Chip,
	Icon,
	TabItem,
	Tabs,
	TabsList,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import BuyerTransactionHistory from "./BuyerTransactionHistory";
import RelationsSingleAction, {
	type RelationRow,
} from "./RelationsSingleAction";
import { BalanceAdjustmentDialog } from "./components";

type RelationTab = "orders" | "payments" | "transactions";
type RelationParty = "buyer" | "seller";

const RelationDetails = ({
	relationId,
	party,
}: {
	relationId: string;
	party: RelationParty;
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [tab, setTab] = useState<RelationTab>("orders");
	const [adjustmentRelation, setAdjustmentRelation] =
		useState<RelationRow | null>(null);
	const {
		data: relation,
		isLoading,
		error,
		refetch,
	} = useRelation({
		path: { relation_id: relationId },
	});
	const decideRelation = useDecideRelation();
	const backTo = party === "buyer" ? "/relations/buyer" : "/relations/seller";
	const backLabel =
		party === "buyer"
			? t("relation.detail.back")
			: t("relation.detail.backToSellers");

	if (isLoading) {
		return (
			<PageLayout>
				<Typography>{t("common.loading")}</Typography>
			</PageLayout>
		);
	}

	if (error || !relation) {
		return (
			<PageLayout>
				<Button
					variant="text"
					startIcon={<Icon name="arrow-right-line" />}
					onClick={() => navigate({ to: backTo })}
				>
					{backLabel}
				</Button>
				<Typography className="mt-4 text-danger-text">
					{party === "buyer"
						? t("relation.detail.loadError")
						: t("relation.detail.loadSellerError")}
				</Typography>
			</PageLayout>
		);
	}

	const status = getRelationStateTranslation(relation.state, t);
	const toggleBan = (id: string, action: "BAN" | "UNBAN") =>
		decideRelation.mutate(
			{ path: { relation_id: id }, body: { action: { action_type: action } } },
			{
				onSuccess: () => {
					toast.success(
						action === "BAN"
							? t("relation.successBanAction")
							: t("relation.successUnbanAction"),
					);
					refetch();
				},
			},
		);

	return (
		<PageLayout>
			<div className="mb-6 flex flex-col gap-5">
				<div className="flex flex-col gap-4 rounded-xl border border-secondary-border bg-background-100 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
					<div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:gap-5">
						<Button
							variant="text"
							color="ghost"
							size="sm"
							startIcon={<Icon name="arrow-left-line" />}
							className="shrink-0 self-start"
							onClick={() => navigate({ to: backTo })}
						>
							{backLabel}
						</Button>
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-2 sm:gap-3">
								<Typography
									variant="title-md"
									as="h1"
									className="break-all sm:break-normal"
								>
									{party === "buyer"
										? relation.buyer_display_name
										: relation.seller_display_name}
								</Typography>
								<Chip
									variant={status.color === "primary" ? "info" : status.color}
									size="sm"
								>
									{status.text}
								</Chip>
							</div>
						</div>
					</div>
					<div className="flex flex-wrap items-center justify-end gap-2">
						<RelationsSingleAction
							selectedRelation={relation}
							banPending={decideRelation.isPending}
							onToggleBan={toggleBan}
							onAdjust={setAdjustmentRelation}
							display="buttons"
							showAdjust={party === "buyer"}
							showEdit={party === "buyer"}
						/>
						<AddPaymentDialog
							direction={party === "buyer" ? "receive" : "send"}
							relationId={relation.id}
							trigger={(open) => (
								<Button
									type="button"
									size="sm"
									startIcon={
										<Icon
											name={
												party === "buyer"
													? "hand-coin-line"
													: "money-dollar-circle-line"
											}
										/>
									}
									onClick={open}
								>
									{party === "buyer"
										? t("payment.form.receiveTitle")
										: t("payment.form.title")}
								</Button>
							)}
						/>
					</div>
				</div>

				<div className="grid gap-3 sm:grid-cols-2">
					<Card className="p-4">
						<Typography variant="caption" className="text-secondary-text-100">
							{t("relation.detail.currentDebt")}
						</Typography>
						<Typography variant="title-md" className="mt-1 tabular-nums">
							{formatNumber(relation.current_debt, "price")}
						</Typography>
					</Card>
					<Card className="p-4">
						<Typography variant="caption" className="text-secondary-text-100">
							{t("relation.detail.creditLimit")}
						</Typography>
						<Typography variant="title-md" className="mt-1 tabular-nums">
							{relation.max_debt == null
								? "−"
								: formatNumber(relation.max_debt, "price")}
						</Typography>
					</Card>
				</div>
			</div>

			<Tabs value={tab} onValueChange={setTab}>
				<TabsList className="overflow-x-auto">
					<TabItem value="orders" Icon={<Icon name="file-list-3-line" />}>
						{t("relation.detail.tabs.orders")}
					</TabItem>
					<TabItem value="payments" Icon={<Icon name="bank-card-line" />}>
						{t("relation.detail.tabs.payments")}
					</TabItem>
					<TabItem value="transactions" Icon={<Icon name="exchange-line" />}>
						{t("relation.detail.tabs.transactions")}
					</TabItem>
				</TabsList>
			</Tabs>

			{tab === "orders" && (
				<OrdersContent
					schemaType={party === "buyer" ? "seller" : "buyer"}
					buyerId={party === "buyer" ? relation.buyer_id : undefined}
					sellerId={party === "seller" ? relation.seller_id : undefined}
					showFilters={false}
				/>
			)}
			{tab === "payments" &&
				(party === "buyer" ? (
					<PaymentReceived relationId={relation.id} />
				) : (
					<PaymentSent relationId={relation.id} />
				))}
			{tab === "transactions" && (
				<BuyerTransactionHistory relationId={relation.id} />
			)}

			{adjustmentRelation && (
				<BalanceAdjustmentDialog
					relation={adjustmentRelation}
					open
					onClose={() => setAdjustmentRelation(null)}
				/>
			)}
		</PageLayout>
	);
};

const BuyerDetails = ({ relationId }: { relationId: string }) => (
	<RelationDetails relationId={relationId} party="buyer" />
);

export const SellerDetails = ({ relationId }: { relationId: string }) => (
	<RelationDetails relationId={relationId} party="seller" />
);

export default BuyerDetails;
